import React, { useCallback, useState } from "react";

import { describeVideoCommentError } from "../../../Hooks/Comments/Video/useVideoComments";
import CommentDialog from "./CommentDialog";

interface DeleteVideoCommentDialogProps {
    requiresReason: boolean;
    onClose: () => void;
    onDelete: (reason?: string) => Promise<unknown>;
    onFocusChange?: (focused: boolean) => void;
}

const DeleteVideoCommentDialog: React.FC<DeleteVideoCommentDialogProps> = ({
    requiresReason,
    onClose,
    onDelete,
    onFocusChange,
}) => {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const close = useCallback(() => {
        if (!submitting) onClose();
    }, [onClose, submitting]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (submitting || (requiresReason && !reason.trim())) return;
        setSubmitting(true);
        setError(null);
        try {
            await onDelete(requiresReason ? reason.trim() : undefined);
            onClose();
        } catch (submitError) {
            setError(describeVideoCommentError(submitError));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <CommentDialog
            title="Delete comment?"
            description="This cannot be undone. Replies may keep a deleted placeholder visible."
            onClose={close}
            onFocusChange={onFocusChange}
        >
            <form className="video-comments__dialog-form" onSubmit={submit}>
                {requiresReason && (
                    <label>
                        Moderator reason
                        <textarea
                            value={reason}
                            rows={4}
                            maxLength={1000}
                            required
                            onChange={(event) => setReason(event.target.value)}
                        />
                    </label>
                )}
                {error && (
                    <p className="video-comments__form-error" role="alert">
                        {error}
                    </p>
                )}
                <div className="video-comments__dialog-actions">
                    <button type="button" className="is-secondary" onClick={close}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="is-danger"
                        disabled={submitting || (requiresReason && !reason.trim())}
                    >
                        {submitting ? "Deleting…" : "Delete"}
                    </button>
                </div>
            </form>
        </CommentDialog>
    );
};

export default DeleteVideoCommentDialog;
